import axiosInstance from "./api";
import config from "../config/index";
import { clearSession } from "./session";
import { intakeLoginTarget } from "../util/intakeLoginTarget";
const cookies = require("js-cookie");

// Endpoints that must never trigger the refresh-and-retry flow: the refresh call
// itself (a failed refresh must not recurse into another refresh) and the 2FA
// step call. Failures there are handled by the caller.
const AUTH_BYPASS = ["/v1/auth/token", "/v1/auth/step"];

const setup = () => {
  axiosInstance.interceptors.request.use(
    (config) => {
      // An Authorization set by the caller wins: OTP.vue's wachtwoord-aanmaken
      // step posts register_otp with the token check_otp handed back, which is
      // deliberately NOT in a cookie yet (the user is mid-flow). A stale
      // fmw_access_token must not be able to redirect that call onto another
      // account. See ../../docs/adr/0033-register-otp-authenticates-the-caller.md.
      let token = cookies.get("fmw_access_token");
      // .get() is case-insensitive; the bracket read is the pre-axios-1 fallback.
      const explicitAuth = config.headers
        ? typeof config.headers.get === "function"
          ? config.headers.get("Authorization")
          : config.headers["Authorization"]
        : null;
      // A header this interceptor set itself is the cookie's, so the retry after
      // a refresh takes the new token. Keeping the old one sent a revoked token:
      // the public page reads then answered as for a visitor without a session,
      // and a logged-in non-member got the Etalage with their own name beside it.
      if (token && (!explicitAuth || config._cookieAuth)) {
        config.headers["Authorization"] = "bearer " + token; // for Spring Boot back-end
        config._cookieAuth = true;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Single-flight refresh. The backend rotates the refresh token on every refresh
  // (ADR-0017): if concurrent 401s each POST /v1/auth/token with the same token,
  // the first succeeds and deletes it and the rest come back {success:false} —
  // which would nuke a still-valid session. So all concurrent 401s share one
  // in-flight refresh and then retry. Since ADR-0017 that costs this tab its own
  // session only; the user's other devices are no longer collateral.
  let refreshPromise = null;
  let teardownStarted = false;

  const runRefresh = async () => {
    try {
      const res = await axiosInstance.post("/v1/auth/token", {
        refresh_token: cookies.get("fmw_refresh_token"),
      });
      if (res.data && res.data.success) {
        const opts = { domain: config.cookiedomain };
        cookies.set("fmw_refresh_token", res.data.data.refresh_token, opts);
        cookies.set("fmw_access_token", res.data.data.access_token, opts);
        return { ok: true };
      }
      // 200 {success:false} => the refresh token is definitively invalid.
      return { ok: false, definitive: true };
    } catch (e) {
      const status = e && e.response ? e.response.status : null;
      // 401/403 => definitively unauthorized. 5xx / timeout / network => transient:
      // a possibly-valid session must NOT be torn down over a deploy blip.
      return { ok: false, definitive: status === 401 || status === 403 };
    }
  };

  const teardownAndRedirect = () => {
    if (teardownStarted) {
      return;
    }
    teardownStarted = true;
    clearSession();
    const subdomain = location.hostname.split(".", 1)[0];
    const origin = config.url.login.replace("{{domain}}", subdomain);
    // Preserve intake context so re-auth returns the user to their form; every
    // other route carries a Terugkeerpad to the generic login screen instead.
    // Both carry the expired notice.
    location.replace(
      `${origin}${intakeLoginTarget(location.pathname, location.search)}`
    );
  };

  axiosInstance.interceptors.response.use(
    (res) => {
      return res;
    },
    async (err) => {
      const originalConfig = err.config;

      const isRefreshable =
        originalConfig &&
        !AUTH_BYPASS.includes(originalConfig.url) &&
        err.response &&
        err.response.status === 401 &&
        !originalConfig._retry &&
        cookies.get("fmw_refresh_token");

      if (!isRefreshable) {
        return Promise.reject(err);
      }

      originalConfig._retry = true;

      if (!refreshPromise) {
        refreshPromise = runRefresh().finally(() => {
          refreshPromise = null;
        });
      }

      const result = await refreshPromise;

      if (result.ok) {
        return axiosInstance(originalConfig);
      }

      if (result.definitive) {
        teardownAndRedirect();
      }

      return Promise.reject(err);
    }
  );
};

export default setup;
